import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box } from '@mui/material'
import useShopSmartMeter from 'src/Libs/shop/hooks/use-shop-smart-meter'
import { getSmartMeterIdDocumentsFromCart } from 'src/Libs/shop/utils/smart-meter'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { makeStyles } from 'tss-react/mui'
import { ShopCheckoutSideBarResponse } from 'src/Libs/shop/models'
import { SmartMeterDocument } from 'src/Libs/shop/models/system-configure-checkout-overview.model'
import SmartMeterForm, { SmartMeterFormValues } from './SmartMeterForm'
import SmartMeterUpload, { SmartMeterFileProps } from './SmartMeterUpload'
import { Formik } from 'formik'
import useSmartMeterValidation from 'src/Libs/shop/hooks/validation/use-smart-meter-validation'
import { LoadingButton } from '@mui/lab'

interface AddNewSmartMeterProps {
  smartMeterCartId: string
  onCartResponse?: (cartData: ShopCheckoutSideBarResponse | null) => void
  handleUploadedFiles: (uploadedFiles: SmartMeterDocument[] | null) => void
}

const useStyles = makeStyles()(theme => ({
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  container: {
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}))

/*
 * This component renders the add new smart meter form
 */
const AddNewSmartMeter: React.FC<AddNewSmartMeterProps> = ({ smartMeterCartId, handleUploadedFiles, onCartResponse }) => {
  const { addSmartMeterContract, isLoading } = useShopSmartMeter()
  const { initialValues, validationSchema } = useSmartMeterValidation()
  const { t } = useCustomTranslation()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { classes } = useStyles()
  const [selectedContract, setSelectedContract] = useState<SmartMeterFileProps>({ file: null, fileName: '' })

  const handleContractSubmit = (values: SmartMeterFormValues, selectedContract: SmartMeterFileProps, resetForm: () => void): void => {
    if (selectedContract.file) {
      addSmartMeterContract(smartMeterCartId, selectedContract.file, values)
        .then(result => {
          if (!result) {
            return enqueueErrorStack(t('shop.smart-meter-document.upload-failed'))
          }
          const smartMeterCartDocuments = getSmartMeterIdDocumentsFromCart(result.lineItems, smartMeterCartId)
          handleUploadedFiles(smartMeterCartDocuments?.smartMeterDocuments ?? null)
          setSelectedContract({ file: null, fileName: '' })
          enqueueSuccessStack(t('shop.smart-meter-document.upload-success'))
          resetForm()
          if (onCartResponse) {
            onCartResponse(result)
          }
        })
        .catch(error => {
          console.error(error)
        })
    }
  }

  const handleFileChange = (file: SmartMeterFileProps): void => {
    setSelectedContract(file)
  }

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.container}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => handleContractSubmit(values, selectedContract, resetForm)}
        >
          {({ isValid, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Box className={classes.formContainer}>
                <SmartMeterUpload selectedFile={selectedContract} onFileChange={handleFileChange} />
                <SmartMeterForm />
                <LoadingButton loading={isLoading} type="submit" disabled={!isValid || !selectedContract.file} variant="contained">
                  {t('shop.smart-meter-document.add-label')}
                </LoadingButton>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Box>
  )
}

export default AddNewSmartMeter
